/**
 * saglayici.ts
 *
 * NE İŞE YARAR : Personelin e-posta/parolasını kimlik sağlayıcısında doğrulayıp
 *                `AU-01`in beklediği belirteci üretir
 * KAYNAK SPEC  : ADR-019 §1 (iki kimlik kaynağı) · §2 (AK-09 — yalnız e-posta gider)
 *                04-api-sozlesmesi.md AU-01 (`firebase_id_token`)
 *                ADR-018 §8 (kimlik sağlayıcı port/adapter arkasındadır)
 *                15-arayuz-standardi.md §15
 * BAĞLI OLDUĞU : Kimlik sağlayıcısının REST yüzeyi · `VITE_KIMLIK_SAGLAYICI_ANAHTARI`
 * KULLANAN     : src/ekran/kimlik/giris.tsx
 * DİKKAT       : 1) SAĞLAYICIYA YALNIZ E-POSTA VE PAROLA GİDER — **AK-09'un teknik
 *                   karşılığı budur** (ADR-019 §2). Bu dosyaya telefon, ad, kimlik
 *                   numarası veya herhangi bir kurum verisi taşıyan bir alan
 *                   EKLENEMEZ. Arka uçtaki eşdeğeri kapı **G14** ile denetleniyor
 *                   (`src/kimlik/portlar.ts` notu 2); istemci tarafındaki tek koruma
 *                   bu nottur, çünkü isteği **tarayıcı** yapar.
 *                2) SDK KULLANILMIYOR, REST KULLANILIYOR. Sağlayıcının tarayıcı SDK'sı
 *                   yüzlerce dosyalık bir bağımlılıktır ve bu ekranın ihtiyacı tek bir
 *                   çağrı: e-posta+parola → belirteç. Yetki hiçbir zaman bu belirteçten
 *                   okunmaz (ADR-008); belirteç yalnız `AU-01`e taşınır ve orada **bir
 *                   kez** doğrulanır. Google/Apple ile giriş SDK'nın açılır pencere
 *                   akışını gerektirir; o yüzden **henüz yazılmadı** ve düğmeleri
 *                   gösterilmiyor — yapılmayan bir şeyin düğmesini koymak, çalıştığını
 *                   söylemektir.
 *                3) ANAHTAR YOKSA SESSİZ GEÇİLMEZ. Kurulumda anahtar tanımlı değilse
 *                   `hazirMi()` `false` döner ve ekran bunu **açıkça** yazar. Boş bir
 *                   anahtarla istek atılsaydı kullanıcı, sebebi sağlayıcıda olan bir
 *                   hatayı kendi parolasının hatası sanardı.
 *                4) SAĞLAYICININ HATA METNİ KULLANICIYA GÖSTERİLMEZ. `EMAIL_NOT_FOUND`
 *                   ile `INVALID_PASSWORD` ayrımı, `AU-01`in ADR-008 §8 gereği özenle
 *                   kapattığı ayrımın ta kendisidir; buradan sızarsa ekran bir kullanıcı
 *                   sayım aracına dönerdi. Bu yüzden başarısızlık **tek bir `null`**tur.
 * SON GÜNCELLEME: 2026-08-31 12:40:00
 */

/**
 * Sağlayıcının parola ile giriş uç noktası.
 *
 * Anahtar **derlemeye gömülmez**: ortam değişkeninden okunur ki her kurulum kendi
 * kimlik projesini kullanabilsin. Gömülseydi her ortam için ayrı bir derleme gerekirdi
 * (`vite.config.ts` notu 2 ile aynı gerekçe).
 */
// Ortam sözlüğü tipsizdir; okunan değer metne daraltılır.
const ANAHTAR: string = String(import.meta.env['VITE_KIMLIK_SAGLAYICI_ANAHTARI'] ?? '');

const UC = 'https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword';

/** Kurulumda kimlik sağlayıcısı yapılandırılmış mı (başlıktaki not 3). */
export function hazirMi(): boolean {
  return ANAHTAR !== '';
}

/**
 * E-posta ve parolayı sağlayıcıda doğrular; `AU-01`e verilecek belirteci döndürür.
 *
 * Başarısızlıkta **`null`** döner — sebebi taşımaz (başlıktaki not 4). Sağlayıcıya hiç
 * ulaşılamadığında da `null` döner: kullanıcı için ikisi de *"giriş olmadı"*dır ve
 * ekran tek bir mesaj gösterir.
 */
export async function epostaIleBelirtecAl(eposta: string, parola: string): Promise<string | null> {
  if (!hazirMi()) {
    return null;
  }

  try {
    const yanit = await fetch(`${UC}?key=${encodeURIComponent(ANAHTAR)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Gövdede **yalnız** bu iki alan bulunur (başlıktaki not 1).
      body: JSON.stringify({ email: eposta, password: parola, returnSecureToken: true }),
    });

    if (!yanit.ok) {
      return null;
    }

    const govde = (await yanit.json()) as { idToken?: unknown };
    return typeof govde.idToken === 'string' && govde.idToken !== '' ? govde.idToken : null;
  } catch {
    return null;
  }
}
